const chapter = {
  id: 'gravitation',
  slug: 'gravitation',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Gravitation',
        hindi: 'गुरुत्वाकर्षण',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Mechanics', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'newton-grav',
      title: {
        english: 'Newton\'s Law of Universal Gravitation',
        hindi: 'न्यूटन का सार्वभौमिक गुरुत्वाकर्षण का नियम',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Newton\'s law of gravitation',
        hindi: 'न्यूटन का गुरुत्वाकर्षण नियम',
          },
          statement: {
            english: `Every two point masses $m_1, m_2$ separated by distance $r$ attract each other with force
$$F = G\\dfrac{m_1 m_2}{r^2}, \\qquad G = 6.67 \\times 10^{-11}\\ \\text{N·m}^2/\\text{kg}^2.$$
Force is along the line joining them.`,
            hindi: 'प्रत्येक दो बिंदु द्रव्यमान $m_1, m_2$ दूरी $r$ से अलग होकर एक दूसरे को बल से आकर्षित करते हैं $$F = G\\dfrac{m_1 m_2}{r^2}, \\qquad G = 6.67 \\times 10^{-11}\\\\\\\\ \\text{N·m}^2/\\text{kg}^2.$$ बल उन्हें जोड़ने वाली रेखा पर है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Gravitation is **always attractive**, **central**, **conservative**.',
        hindi: 'गुरुत्वाकर्षण **हमेशा आकर्षक**, **केंद्रीय**, **रूढ़िवादी** होता है।',
            },
            {
              english: 'For a uniform spherical shell: at external point, behaves as point mass at centre. At internal point, force = 0 (shell theorem).',
        hindi: 'एक समान गोलाकार खोल के लिए: बाहरी बिंदु पर, केंद्र पर बिंदु द्रव्यमान के रूप में व्यवहार करता है। आंतरिक बिंदु पर, बल = 0 (shell theorem)।',
            },
            {
              english: 'For a uniform solid sphere: external — point mass at centre; internal at distance $r < R$: $F \\propto r$.',
        hindi: 'एक समान ठोस गोले के लिए: बाहरी - केंद्र में बिंदु द्रव्यमान; आंतरिक दूरी पर $r < R$: $F \\propto r$। **सतह से ऊंचाई $r < R$**: के लिए $F \\propto r$।',
            },
          ],
        },
      ],
    },
    {
      id: 'g-variation',
      title: {
        english: 'Acceleration Due to Gravity & Its Variation',
        hindi: 'गुरुत्वीय त्वरण और इसकी विविधता',
      },
      blocks: [
        {
          type: 'equation',
          latex: 'g = \\dfrac{GM}{R^2}\\ \\text{(at Earth\\u2019s surface)} \\approx 9.8\\ \\text{m/s}^2.',
        },
        {
          type: 'list',
          items: [
            {
              english: '**Height $h$ above surface**: $g_h = g\\dfrac{R^2}{(R+h)^2} \\approx g\\left(1 - \\dfrac{2h}{R}\\right)$ for $h \\ll R$.',
        hindi: '**सतह से ऊँचाई $h$**: $h \\ll R$ के लिए $g_h = g\\dfrac{R^2}{(R+h)^2} \\approx g\\left(1 - \\dfrac{2h}{R}\\right)$।',
            },
            {
              english: '**Depth $d$ below surface**: $g_d = g\\left(1 - \\dfrac{d}{R}\\right)$. At centre, $g = 0$.',
        hindi: '**सतह के नीचे गहराई $d$**: $g_d = g\\left(1 - \\dfrac{d}{R}\\right)$. केंद्र में, $g = 0$।',
            },
            {
              english: '**Latitude $\\lambda$ (rotation effect)**: $g_\\lambda = g - \\omega^2 R\\cos^2\\lambda$. Maximum at poles, minimum at equator.',
        hindi: '**अक्षांश $\\lambda$ (रोटेशन प्रभाव)**: $g_\\lambda = g - \\omega^2 R\\cos^2\\lambda$। ध्रुवों पर अधिकतम, भूमध्य रेखा पर न्यूनतम।',
            },
            {
              english: '**Shape effect**: Earth oblate; $R_{\\text{eq}} > R_{\\text{pole}} \\Rightarrow g_{\\text{pole}} > g_{\\text{eq}}$.',
        hindi: '**आकार प्रभाव**: पृथ्वी चपटी; $R_{\\text{eq}} > R_{\\text{pole}} \\Rightarrow g_{\\text{pole}} > g_{\\text{eq}}$.',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'At what height $h$ does $g$ become $g/4$? ($R = $ Earth radius)',
        hindi: '$h$ किस ऊंचाई पर $g$ $g/4$ बन जाता है? ($R = $ पृथ्वी त्रिज्या)',
          },
          solution: {
            english: '$g/4 = gR^2/(R+h)^2 \\Rightarrow (R+h)^2 = 4R^2 \\Rightarrow h = R$.',
        hindi: '$g/4 = gR^2/(R+h)^2 \\Rightarrow (R+h)^2 = 4R^2 \\Rightarrow h = R$.',
          },
        },
      ],
    },
    {
      id: 'pe-grav',
      title: {
        english: 'Gravitational Potential Energy & Potential',
        hindi: 'गुरुत्वीय स्थितिज ऊर्जा और विभव',
      },
      blocks: [
        {
          type: 'equation',
          latex: 'U(r) = -\\dfrac{G M m}{r}\\ \\text{(taking } U(\\infty) = 0\\text{)}.',
        },
        {
          type: 'definition',
          term: {
            english: 'Gravitational potential',
        hindi: 'गुरुत्वाकर्षण क्षमता',
          },
          body: {
            english: '$V(r) = U/m = -GM/r$. At surface: $V_s = -GM/R$.',
        hindi: '$V(r) = U/m = -GM/r$. सतह पर: $V_s = -GM/R$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Solid sphere, internal** ($r < R$): $V = -\\dfrac{GM}{2R^3}(3R^2 - r^2)$. Centre: $V_c = -3GM/(2R)$.',
        hindi: '**ठोस गोला, आंतरिक** ($r < R$): $V = -\\dfrac{GM}{2R^3}(3R^2 - r^2)$. केंद्र: $V_c = -3GM/(2R)$.',
            },
            {
              english: '**Shell, internal**: $V = -GM/R$ (constant), field $= 0$.',
        hindi: '**शैल, आंतरिक**: $V = -GM/R$ (स्थिर), फ़ील्ड $= 0$।',
            },
            {
              english: 'PE of system of two masses: $U_{12} = -G m_1 m_2 / r_{12}$. For three: pairwise sum.',
        hindi: 'दो द्रव्यमानों की प्रणाली का PE: $U_{12} = -G m_1 m_2 / r_{12}$। तीन के लिए: जोड़ीवार योग.',
            },
            {
              english: 'Work done by external agent to take mass from $r_1$ to $r_2$ (slowly) $= U(r_2) - U(r_1)$.',
        hindi: 'द्रव्यमान को $r_1$ से $r_2$ (धीरे-धीरे) $= U(r_2) - U(r_1)$ तक ले जाने के लिए बाहरी एजेंट द्वारा किया गया कार्य।',
            },
          ],
        },
      ],
    },
    {
      id: 'escape-orbit',
      title: {
        english: 'Escape Velocity & Orbital Motion',
        hindi: 'पलायन वेग और कक्षीय गति',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Escape velocity',
        hindi: 'पलायन वेग',
          },
          statement: {
            english: `Minimum speed at surface so that a body just escapes Earth's gravity (reaches infinity with zero KE):
$$v_e = \\sqrt{\\dfrac{2GM}{R}} = \\sqrt{2 g R} \\approx 11.2\\ \\text{km/s}.$$`,
            hindi: 'सतह पर न्यूनतम गति ताकि कोई पिंड पृथ्वी के गुरुत्वाकर्षण से बच जाए (शून्य KE के साथ अनंत तक पहुंच जाए): $$v_e = \\sqrt{\\dfrac{2GM}{R}} = \\sqrt{2 g R} \\approx 11.2\\\\\\\\ \\text{km/s}.$$',
          },
          proof: {
            english: 'Energy conservation: $\\tfrac12 m v_e^2 - GMm/R = 0 + 0 \\Rightarrow v_e = \\sqrt{2GM/R}$.',
        hindi: 'ऊर्जा संरक्षण: $\\tfrac12 m v_e^2 - GMm/R = 0 + 0 \\Rightarrow v_e = \\sqrt{2GM/R}$.',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Orbital velocity (circular orbit, radius $r$)',
        hindi: 'कक्षीय वेग (गोलाकार कक्षा, त्रिज्या $r$)',
          },
          statement: {
            english: '$\\dfrac{m v_o^2}{r} = \\dfrac{GMm}{r^2} \\Rightarrow v_o = \\sqrt{\\dfrac{GM}{r}}$. For surface orbit ($r = R$): $v_o = \\sqrt{gR} \\approx 7.9$ km/s.',
        hindi: '$\\dfrac{m v_o^2}{r} = \\dfrac{GMm}{r^2} \\Rightarrow v_o = \\sqrt{\\dfrac{GM}{r}}$. सतह कक्षा ($r = R$) के लिए: $v_o = \\sqrt{gR} \\approx 7.9$ किमी/सेकेंड।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Relation: $v_e = \\sqrt{2}\\,v_o$ (for surface orbit).',
        hindi: 'संबंध: $v_e = \\sqrt{2}\\,v_o$ (सतह कक्षा के लिए)।',
            },
            {
              english: 'Time period: $T = 2\\pi\\sqrt{r^3/(GM)}$ → **Kepler\'s 3rd law** $T^2 \\propto r^3$.',
        hindi: 'समयावधि: $T = 2\\pi\\sqrt{r^3/(GM)}$ → **केप्लर का तीसरा नियम** $T^2 \\propto r^3$।',
            },
            {
              english: 'Total energy of satellite in orbit: $E = -GMm/(2r) = -K = U/2$. Negative ⟹ bound.',
        hindi: 'कक्षा में उपग्रह की कुल ऊर्जा: $E = -GMm/(2r) = -K = U/2$. नकारात्मक ⟹ बंधा हुआ।',
            },
            {
              english: '**Geostationary orbit**: period 24 h, equatorial, height ≈ $36{,}000$ km.',
        hindi: '**जियोस्टेशनरी कक्षा**: अवधि 24 घंटे, भूमध्यरेखीय, ऊँचाई ≈ $36{,}000$ किमी।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Energy required to raise a satellite of mass $m$ from surface to a circular orbit at height $R$ above surface.',
        hindi: '$m$ द्रव्यमान के उपग्रह को सतह से $R$ ऊंचाई पर एक गोलाकार कक्षा में ले जाने के लिए आवश्यक ऊर्जा।',
          },
          solution: {
            english: '$E_i = -GMm/R$. $E_f = -GMm/(2 \\cdot 2R) = -GMm/(4R)$. $\\Delta E = E_f - E_i = 3GMm/(4R)$.',
        hindi: '$E_i = -GMm/R$. $E_f = -GMm/(2 \\cdot 2R) = -GMm/(4R)$. $\\Delta E = E_f - E_i = 3GMm/(4R)$.',
          },
        },
      ],
    },
    {
      id: 'kepler',
      title: {
        english: 'Kepler\'s Laws',
        hindi: 'केप्लर के नियम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**1st (Law of orbits)**: Each planet moves in an ellipse with the Sun at one focus.',
        hindi: '**पहला (कक्षाओं का नियम)**: प्रत्येक ग्रह सूर्य के एक फोकस पर रहते हुए एक दीर्घवृत्त में घूमता है।',
            },
            {
              english: '**2nd (Law of areas)**: The line joining a planet to the Sun sweeps out equal areas in equal times. ⇔ angular momentum is conserved (areal velocity $dA/dt = L/(2m) = $ const).',
        hindi: '**दूसरा (क्षेत्रफल का नियम)**: किसी ग्रह को सूर्य से जोड़ने वाली रेखा समान समय में समान क्षेत्रफल तय करती है। ⇔ कोणीय संवेग संरक्षित रहता है (क्षेत्रीय वेग $dA/dt = L/(2m) = $ स्थिरांक)।',
            },
            {
              english: '**3rd (Law of periods)**: $T^2 \\propto a^3$, where $a$ is the semi-major axis. Same proportionality constant for all planets around a given Sun.',
        hindi: '**तीसरा (अवधि का नियम)**: $T^2 \\propto a^3$, जहां $a$ अर्ध-प्रमुख अक्ष है। किसी दिए गए सूर्य के चारों ओर सभी ग्रहों के लिए समान आनुपातिकता स्थिरांक।',
            },
          ],
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Two satellites orbit Earth at radii $r$ and $4r$. Ratio of their periods?',
        hindi: 'दो उपग्रह पृथ्वी की त्रिज्या $r$ और $4r$ पर परिक्रमा करते हैं। उनकी अवधियों का अनुपात?',
          },
          solution: {
            english: '$T \\propto r^{3/2}$. $T_2/T_1 = 4^{3/2} = 8$.',
        hindi: '$T \\propto r^{3/2}$. $T_2/T_1 = 4^{3/2} = 8$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'F = G m_1 m_2 / r^2',
      desc: {
        english: 'Newton\'s law',
        hindi: 'न्यूटन का नियम',
      },
    },
    {
      latex: 'g = GM/R^2',
      desc: {
        english: 'Surface gravity',
        hindi: 'सतही गुरुत्वाकर्षण',
      },
    },
    {
      latex: 'g_h \\approx g(1 - 2h/R),\\ g_d = g(1 - d/R)',
      desc: {
        english: 'Variation',
        hindi: 'भिन्नता',
      },
    },
    {
      latex: 'U = -GMm/r,\\ V = -GM/r',
      desc: {
        english: 'PE & potential',
        hindi: 'पीई और क्षमता',
      },
    },
    {
      latex: 'v_e = \\sqrt{2GM/R}',
      desc: {
        english: 'Escape velocity',
        hindi: 'पलायन वेग',
      },
    },
    {
      latex: 'v_o = \\sqrt{GM/r}',
      desc: {
        english: 'Orbital velocity',
        hindi: 'कक्षीय वेग',
      },
    },
    {
      latex: 'T^2 \\propto r^3',
      desc: {
        english: 'Kepler III',
        hindi: 'केप्लर III',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'g-variation',
      problem: {
        english: 'At what depth below Earth\'s surface is acceleration due to gravity 25% of surface value?',
        hindi: 'पृथ्वी की सतह के नीचे कितनी गहराई पर गुरुत्वाकर्षण के कारण त्वरण सतह मान का 25% है?',
      },
      answer: {
        english: '$d = 3R/4$',
        hindi: '$d = 3R/4$',
      },
      solution: {
        english: '$g_d/g = 1 - d/R = 1/4 \\Rightarrow d = 3R/4$.',
        hindi: '$g_d/g = 1 - d/R = 1/4 \\Rightarrow d = 3R/4$।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Escape',
      problem: {
        english: 'Escape velocity from a planet of mass $4M$ and radius $2R$ (in terms of $v_e$ from Earth)?',
        hindi: 'द्रव्यमान $4M$ और त्रिज्या $2R$ (पृथ्वी से $v_e$ के संदर्भ में) वाले ग्रह से पलायन वेग?',
      },
      answer: {
        english: '$\\sqrt{2}\\,v_e$',
        hindi: '$\\sqrt{2}\\,v_e$',
      },
      solution: {
        english: '$v_e\' = \\sqrt{2G(4M)/(2R)} = \\sqrt{2}\\sqrt{2GM/R} = \\sqrt{2}v_e$.',
        hindi: '$v_e\' = \\sqrt{2G(4M)/(2R)} = \\sqrt{2}\\sqrt{2GM/R} = \\sqrt{2}v_e$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Satellite energy',
      problem: {
        english: 'Total energy of a satellite of mass $m$ in circular orbit of radius $r$ around Earth.',
        hindi: 'पृथ्वी के चारों ओर त्रिज्या $r$ की गोलाकार कक्षा में द्रव्यमान $m$ के उपग्रह की कुल ऊर्जा।',
      },
      answer: {
        english: '$-GMm/(2r)$',
        hindi: '$-GMm/(2r)$',
      },
      solution: {
        english: 'KE $= GMm/(2r)$, PE $= -GMm/r$, sum $= -GMm/(2r)$.',
        hindi: 'केई $= जीएमएम/(2आर)$, पीई $= -जीएमएम/आर$, योग $= -जीएमएम/(2आर)$।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Kepler',
      problem: {
        english: 'A planet has mean distance from Sun 4 times that of Earth. Its period (Earth years)?',
        hindi: 'एक ग्रह की सूर्य से औसत दूरी पृथ्वी से 4 गुना है। इसकी अवधि (पृथ्वी वर्ष)?',
      },
      answer: {
        english: '$8$ years',
        hindi: '$8$ वर्ष',
      },
      solution: {
        english: '$T \\propto a^{3/2}$.',
        hindi: '$T \\propto a^{3/2}$।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Potential',
      problem: {
        english: 'Two point masses $m$ each are at distance $2a$ apart. Gravitational potential at midpoint.',
        hindi: 'दो बिंदु द्रव्यमान $m$ प्रत्येक एक दूसरे से $2a$ की दूरी पर हैं। मध्यबिंदु पर गुरुत्वाकर्षण क्षमता.',
      },
      answer: {
        english: '$-2Gm/a$',
        hindi: '$-2Gm/a$',
      },
      solution: {
        english: '$V = -Gm/a - Gm/a = -2Gm/a$.',
        hindi: '$V = -Gm/a - Gm/a = -2Gm/a$.',
      },
    },
  ],
  next: {
    slug: 'properties-of-matter',
    title: 'Mechanical Properties of Solids & Fluids',
  },
};

export default chapter;
